import React from 'react';

import './GameTagsList.css';

type GameTagsListProps = {
  tags: string[];
  mode?: 'display' | 'filter';
  activeTags?: string[];
  onTagClick?: (tag: string) => void;
  compact?: boolean;
  collapsible?: boolean;
};

export function GameTagsList({
  tags,
  mode = 'display',
  activeTags = [],
  onTagClick,
  compact = false,
  collapsible = false,
}: GameTagsListProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [visibleCount, setVisibleCount] = React.useState(tags.length);

  React.useLayoutEffect(() => {
    if (!collapsible || expanded) {
      setVisibleCount(tags.length);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const children = Array.from(container.children) as HTMLElement[];
    if (children.length === 0) return;

    const containerTop = children[0].offsetTop;
    let count = 0;

    for (const child of children) {
      if (child.classList.contains('game-tags__more')) break;
      if (child.offsetTop > containerTop) break;
      count++;
    }

    setVisibleCount(Math.max(1, count));
  }, [collapsible, expanded, tags]);

  if (tags.length === 0) return null;

  const isCollapsed = collapsible && !expanded && visibleCount < tags.length;
  const displayTags = isCollapsed ? tags.slice(0, visibleCount) : tags;
  const hiddenCount = tags.length - visibleCount;

  return (
    <div
      ref={containerRef}
      className={[
        'game-tags',
        compact ? 'game-tags--compact' : '',
        isCollapsed ? 'game-tags--collapsed' : '',
      ].filter(Boolean).join(' ')}
    >
      {displayTags.map((tag) => {
        const isActive = activeTags.includes(tag);
        const className = [
          'game-tag',
          mode === 'filter' ? 'game-tag--filter' : '',
          isActive ? 'game-tag--active' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return mode === 'filter' ? (
          <button key={tag} className={className} onClick={() => onTagClick?.(tag)}>
            {tag}
          </button>
        ) : (
          <span key={tag} className={className}>
            {tag}
          </span>
        );
      })}
      {isCollapsed && (
        <button
          className="game-tags__more"
          onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
        >
          +{hiddenCount}
        </button>
      )}
      {collapsible && expanded && tags.length > 1 && (
        <button
          className="game-tags__more"
          onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
        >
          ↑
        </button>
      )}
    </div>
  );
}
